import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { TOPIC_DISPLAY_LABELS } from "../filterLabels";
import type { FacetCount, GraphEdge, GraphLens, GraphMeta, OntologyItem } from "../types";
import { Icon } from "./Icon";

const MOBILE_SIDEBAR_QUERY = "(max-width: 960px)";

function getInitialMobileSidebar() {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_SIDEBAR_QUERY).matches;
}

const LENSES: Array<{
  id: GraphLens;
  label: string;
  description: string;
}> = [
  { id: "type", label: "유형별", description: "노드 유형의 색으로 보기" },
  { id: "topic", label: "주제별", description: "주제 군집의 색으로 보기" },
  { id: "origin", label: "출처별", description: "정보 출처의 색으로 보기" },
  { id: "label", label: "라벨", description: "모든 노드 이름 펼쳐 보기" },
];

const PRIMARY_TOPIC_COUNT = 6;
const TOPIC_COLORS = ["#5c8ef5", "#a48ae8", "#e1912f", "#69b68d", "#e05a5f", "#8fa3b8"];

function hashString(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

interface LensLegendItem {
  color: string;
  count?: number;
  id: string;
  label: string;
  title: string;
}

interface SidebarProps {
  activeTopics: Set<string>;
  activeTypes: Set<string>;
  edges: GraphEdge[];
  edgeTypes: OntologyItem[];
  highlightedEdgeType: string | null;
  lens: GraphLens;
  meta: GraphMeta;
  mobileOpen: boolean;
  origins?: OntologyItem[];
  topicFacets: FacetCount[];
  typeFacets: FacetCount[];
  onClose: () => void;
  onEdgeTypeToggle: (type: string) => void;
  onLensChange: (lens: GraphLens) => void;
  onTopicToggle: (topic: string) => void;
  onTypeToggle: (type: string) => void;
}

export function Sidebar({
  activeTopics,
  activeTypes,
  edges,
  edgeTypes,
  highlightedEdgeType,
  lens,
  meta,
  mobileOpen,
  origins = [],
  topicFacets,
  typeFacets,
  onClose,
  onEdgeTypeToggle,
  onLensChange,
  onTopicToggle,
  onTypeToggle,
}: SidebarProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [mobileSidebar, setMobileSidebar] = useState(getInitialMobileSidebar);
  const isPublic = meta.scope.toLocaleLowerCase() === "public";
  const inactiveMobileSidebar = mobileSidebar && !mobileOpen;
  const primaryTopics = topicFacets.slice(0, PRIMARY_TOPIC_COUNT);
  const additionalTopics = topicFacets.slice(PRIMARY_TOPIC_COUNT);
  const edgeTypeCounts = new Map<string, number>();
  for (const edge of edges) edgeTypeCounts.set(edge.type, (edgeTypeCounts.get(edge.type) ?? 0) + 1);

  const topicLabel = (facet: FacetCount) => TOPIC_DISPLAY_LABELS[facet.id] ?? facet.label;

  const activeLens = LENSES.find((item) => item.id === lens) ?? LENSES[0];
  const legendItems = useMemo<LensLegendItem[]>(() => {
    if (lens === "type") {
      return typeFacets.map((facet) => ({
        color: facet.color ?? "#8b8f8d",
        count: facet.count,
        id: facet.id,
        label: facet.label,
        title: facet.description ?? facet.label,
      }));
    }

    if (lens === "topic") {
      return topicFacets.map((facet) => ({
        color: TOPIC_COLORS[hashString(facet.id) % TOPIC_COLORS.length],
        count: facet.count,
        id: facet.id,
        label: topicLabel(facet),
        title: facet.id,
      }));
    }

    if (lens === "origin") {
      return origins.map((origin) => ({
        color: origin.color,
        id: origin.id,
        label: origin.label,
        title: origin.description,
      }));
    }

    return [];
  }, [lens, origins, topicFacets, typeFacets]);

  const renderTopicFacet = (facet: FacetCount, index: number) => {
    const active = activeTopics.has(facet.id);
    return (
      <button
        aria-pressed={active}
        className={`facet-row${active ? " is-active" : ""}`}
        key={facet.id}
        onClick={() => onTopicToggle(facet.id)}
        title={facet.id}
        type="button"
      >
        <span
          aria-hidden="true"
          className="facet-dot"
          style={{ "--facet-color": index === 0 ? "#5c8ef5" : "#969a98" } as CSSProperties}
        />
        <span className="facet-label">{topicLabel(facet)}</span>
        <span className="facet-count">{facet.count}</span>
      </button>
    );
  };

  const renderTypeFacet = (facet: FacetCount) => {
    const active = activeTypes.has(facet.id);
    const facetColor = lens === "type" ? facet.color ?? "#8b8f8d" : "#73777c";
    return (
      <button
        aria-pressed={active}
        className={`facet-row${active ? " is-active" : ""}`}
        key={facet.id}
        onClick={() => onTypeToggle(facet.id)}
        title={facet.description}
        type="button"
      >
        <span
          aria-hidden="true"
          className="facet-dot type-facet-dot"
          style={{ "--facet-color": facetColor } as CSSProperties}
        />
        <span className="facet-label">{facet.label}</span>
        <span className="facet-count">{facet.count}</span>
      </button>
    );
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_SIDEBAR_QUERY);
    const updateSidebarMode = () => setMobileSidebar(mediaQuery.matches);

    updateSidebarMode();
    mediaQuery.addEventListener("change", updateSidebarMode);
    return () => mediaQuery.removeEventListener("change", updateSidebarMode);
  }, []);

  useEffect(() => {
    if (!mobileSidebar || !mobileOpen) return;

    const animationFrame = requestAnimationFrame(() => closeButtonRef.current?.focus());
    return () => cancelAnimationFrame(animationFrame);
  }, [mobileOpen, mobileSidebar]);

  return (
    <>
      <button
        aria-label="사이드바 닫기"
        className={`sidebar-backdrop${mobileOpen ? " is-visible" : ""}`}
        onClick={onClose}
        tabIndex={mobileOpen ? 0 : -1}
        type="button"
      />
      <aside
        aria-label="그래프 탐색 필터"
        className={`sidebar${mobileOpen ? " is-open" : ""}`}
        id="graph-sidebar"
        inert={inactiveMobileSidebar}
      >
        <header className="sidebar-header">
          <div className="brand-lockup" aria-label="JH Second Brain">
            <strong>JH</strong>
            <span aria-hidden="true">/</span>
            <span>SECOND BRAIN</span>
          </div>
          <button
            aria-label="사이드바 닫기"
            className="icon-button sidebar-close"
            onClick={onClose}
            ref={closeButtonRef}
            type="button"
          >
            <Icon name="close" />
          </button>
          <h1>{meta.title}</h1>
          <p className={`scope-state${isPublic ? " is-public" : ""}`}>
            <span aria-hidden="true" className="scope-dot" />
            <span>
              {isPublic
                ? `공개 ${meta.nodeCount.toLocaleString("ko-KR")} · 연결 ${meta.edgeCount.toLocaleString("ko-KR")}`
                : `${meta.scope} · 노드 ${meta.nodeCount.toLocaleString("ko-KR")} · 연결 ${meta.edgeCount.toLocaleString("ko-KR")}`}
            </span>
            {isPublic ? (
              <span
                aria-label="민감한 내용은 포함되지 않습니다."
                role="img"
                title="민감한 내용은 포함되지 않습니다."
              >
                <Icon name="info" size={15} />
              </span>
            ) : null}
          </p>
          <section className="facet-section lens-section" aria-labelledby="lens-title">
            <div className="section-title" id="lens-title">
              색상 기준
            </div>
            <div className="lens-grid">
              {LENSES.map((item) => (
                <button
                  aria-pressed={lens === item.id}
                  className={`lens-button${lens === item.id ? " is-active" : ""}`}
                  key={item.id}
                  onClick={() => onLensChange(item.id)}
                  title={item.description}
                  type="button"
                >
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
            <div
              aria-label={`${activeLens.label} 색상 범례`}
              className="lens-legend"
              role="list"
              style={{
                display: "grid",
                gap: "4px 10px",
                gridTemplateColumns: legendItems.length > 6 ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)",
                marginTop: 12,
              }}
            >
              {legendItems.length > 0 ? (
                legendItems.map((item) => (
                  <div
                    className="lens-legend-item"
                    key={item.id}
                    role="listitem"
                    style={{
                      alignItems: "center",
                      color: "#7d7d84",
                      display: "flex",
                      fontSize: 12,
                      gap: 7,
                      minHeight: 27,
                      minWidth: 0,
                    }}
                    title={item.title}
                  >
                    <span
                      aria-hidden="true"
                      className="facet-dot"
                      style={{ "--facet-color": item.color } as CSSProperties}
                    />
                    <span className="facet-label">{item.label}</span>
                    {item.count !== undefined ? (
                      <span className="facet-count" style={{ color: "#64646c", marginLeft: "auto" }}>
                        {item.count}
                      </span>
                    ) : null}
                  </div>
                ))
              ) : (
                <p
                  className="lens-legend-note"
                  style={{ color: "#7d7d84", fontSize: 12, lineHeight: 1.45, margin: 0 }}
                >
                  {lens === "label" ? "색상 없음 · 라벨 전체 표시" : "표시할 색상 정보가 없습니다."}
                </p>
              )}
            </div>
          </section>
        </header>

        <div className="sidebar-scroll-region">
          <details className="facet-section topic-facet-section" open>
            <summary>
              <span>주요 주제</span>
              <Icon name="chevron" size={15} />
            </summary>
            <div className="facet-list topic-facet-list">
              {primaryTopics.map(renderTopicFacet)}
            </div>
          </details>

          {additionalTopics.length > 0 || typeFacets.length > 0 ? (
            <details className="facet-section all-filter-section" open>
              <summary>
                <span>전체 필터</span>
                <Icon name="chevron" size={15} />
              </summary>
              <div className="all-filter-content">
                {additionalTopics.length > 0 ? (
                  <div className="filter-group">
                    <div className="section-title">추가 주제</div>
                    <div className="facet-list topic-facet-list">
                      {additionalTopics.map(renderTopicFacet)}
                    </div>
                  </div>
                ) : null}
                {typeFacets.length > 0 ? (
                  <div className="filter-group type-facet-group">
                    <div className="section-title">노드 유형</div>
                    <div className="facet-list type-facet-list">{typeFacets.map(renderTypeFacet)}</div>
                  </div>
                ) : null}
                {edgeTypes.length > 0 ? (
                  <details className="edge-type-panel">
                    <summary>
                      <span>엣지 유형</span>
                      <Icon name="chevron" size={15} />
                    </summary>
                    <ul className="edge-type-list" aria-label="엣지 유형 범례">
                      {edgeTypes.map((edgeType) => (
                        <li
                          className={`edge-type-row${highlightedEdgeType === edgeType.id ? " is-active" : ""}`}
                          key={edgeType.id}
                        >
                          <button
                            aria-label={`${edgeType.label} 엣지 강조${highlightedEdgeType === edgeType.id ? " 해제" : ""}`}
                            aria-pressed={highlightedEdgeType === edgeType.id}
                            className="edge-type-button"
                            onClick={() => onEdgeTypeToggle(edgeType.id)}
                            title={`${edgeType.description} · 클릭해 강조`}
                            type="button"
                          >
                            <span
                              aria-hidden="true"
                              className="edge-type-swatch"
                              style={{ "--edge-color": edgeType.color } as CSSProperties}
                            />
                            <span className="edge-type-name">{edgeType.label}</span>
                            <code>{edgeType.id}</code>
                            <span className="edge-type-count">
                              {(edgeTypeCounts.get(edgeType.id) ?? 0).toLocaleString("ko-KR")}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    <p className="edge-type-help">
                      유형을 누르면 해당 엣지를 강조합니다. 다시 누르거나 Esc로 해제할 수 있습니다.
                    </p>
                  </details>
                ) : null}
              </div>
            </details>
          ) : null}
        </div>
      </aside>
    </>
  );
}
